import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SearchField, SelectMenu, Button, Popover, Icon, useToast } from '@caos/ca-component-library';
import { getAllTags, getAllMcpTags, getAllMcpEnvironments, getAllOwners, filterSkills, filterMcpServers } from '../lib/marketplace';
import { Skill, McpServer } from '../types';
import { useManifest } from '../context/ManifestContext';
import { useMcpRegistry } from '../context/McpRegistryContext';
import SkillCard from '../components/SkillCard';
import McpServerCard from '../components/McpServerCard';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import usePageTitle from '../hooks/usePageTitle';
import './MarketplacePage.css';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Recently updated' },
  { value: 'alpha', label: 'A – Z' },
];

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'skills', label: 'Skills' },
  { value: 'mcp', label: 'MCP Servers' },
];

export default function MarketplacePage() {
  usePageTitle();
  const { skills, loading: skillsLoading, error: skillsError, refresh, canRefresh, cooldownRemaining } = useManifest();
  const { servers, loading: mcpLoading, error: mcpError } = useMcpRegistry();
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [sort, setSort] = useState<'recent' | 'alpha'>('recent');
  const [activeTab, setActiveTab] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const allTags = useMemo(() => {
    const combined = new Set([...getAllTags(skills), ...getAllMcpTags(servers)]);
    return Array.from(combined).sort();
  }, [skills, servers]);

  const allEnvironments = useMemo(() => getAllMcpEnvironments(servers), [servers]);
  const allOwners = useMemo(() => getAllOwners(skills, servers), [skills, servers]);

  const attachWheelScroll = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    node.addEventListener('wheel', (e: WheelEvent) => {
      if (node.scrollWidth <= node.clientWidth) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        node.scrollLeft += e.deltaY;
      }
    }, { passive: false });
  }, []);

  const showSkills = (activeTab === 'all' || activeTab === 'skills') && selectedEnvironments.length === 0;
  const showMcp = activeTab === 'all' || activeTab === 'mcp';

  const filteredSkills = useMemo(
    () => showSkills ? filterSkills(skills, { search, tags: selectedTags, owners: selectedOwners, sort }) : [],
    [skills, search, selectedTags, selectedOwners, sort, showSkills]
  );

  const filteredServers = useMemo(
    () => showMcp ? filterMcpServers(servers, search, sort, selectedTags, selectedEnvironments, selectedOwners) : [],
    [servers, search, sort, selectedTags, selectedEnvironments, selectedOwners, showMcp]
  );

  const combinedItems = useMemo(() => {
    const skillItems = filteredSkills.map((s) => ({ type: 'skill' as const, item: s, date: s.last_updated, name: s.name }));
    const serverItems = filteredServers.map((s) => ({ type: 'mcp' as const, item: s, date: s.last_updated, name: s.name }));
    const combined = [...skillItems, ...serverItems];
    if (sort === 'recent') {
      combined.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else {
      combined.sort((a, b) => a.name.localeCompare(b.name));
    }
    return combined;
  }, [filteredSkills, filteredServers, sort]);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const toggleEnvironment = (env: string) => {
    setSelectedEnvironments((prev) =>
      prev.includes(env) ? prev.filter((e) => e !== env) : [...prev, env]
    );
  };

  const toggleOwner = (owner: string) => {
    setSelectedOwners((prev) =>
      prev.includes(owner) ? prev.filter((o) => o !== owner) : [...prev, owner]
    );
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedTags([]);
    setSelectedEnvironments([]);
    setSelectedOwners([]);
    setSort('recent');
  };

  const activeFilterCount = selectedTags.length + selectedOwners.length;
  const hasActiveFilters = search || activeFilterCount > 0 || selectedEnvironments.length > 0;
  const loading = skillsLoading || mcpLoading;

  const hasSkills = filteredSkills.length > 0;
  const hasServers = filteredServers.length > 0;

  if (loading) {
    return (
      <div className="marketplace-page">
        <div className="marketplace-grid">
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-page">
  
      <div className="marketplace-controls">
        <div className="marketplace-controls-left">
          <div className="marketplace-search">
            <SearchField
              id="marketplace-search"
              placeholder="Search skills, servers, tools..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              clearable
            />
          </div>
          <button
            ref={filtersButtonRef}
            className="marketplace-filter-button"
            onClick={() => setFiltersOpen((o) => !o)}
          >
            <Icon icon="filter" size="sm" />
            Filters
            {activeFilterCount > 0 && (
              <span className="marketplace-filter-count">{activeFilterCount}</span>
            )}
          </button>
          <Popover
            open={filtersOpen}
            anchorEl={filtersButtonRef.current}
            onClose={() => setFiltersOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <div className="marketplace-filter-panel">
              {allTags.length > 0 && (
                <div className="marketplace-filter-section">
                  <div className="marketplace-filter-section-header">
                    <span className="marketplace-filter-section-title">Tags</span>
                    {selectedTags.length > 0 && (
                      <button className="marketplace-tag-filter-clear" onClick={() => setSelectedTags([])}>Clear</button>
                    )}
                  </div>
                  <div className="marketplace-tag-filter-list">
                    {allTags.map((tag) => {
                      const checked = selectedTags.includes(tag);
                      return (
                        <div key={tag} className={`marketplace-tag-filter-item${checked ? ' checked' : ''}`} onClick={() => toggleTag(tag)}>
                          <span className="marketplace-tag-filter-label">{tag}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {allOwners.length > 0 && (
                <div className="marketplace-filter-section">
                  <div className="marketplace-filter-section-header">
                    <span className="marketplace-filter-section-title">Owners</span>
                    {selectedOwners.length > 0 && (
                      <button className="marketplace-tag-filter-clear" onClick={() => setSelectedOwners([])}>Clear</button>
                    )}
                  </div>
                  <div className="marketplace-tag-filter-list">
                    {allOwners.map((owner) => {
                      const checked = selectedOwners.includes(owner);
                      return (
                        <div key={owner} className={`marketplace-tag-filter-item${checked ? ' checked' : ''}`} onClick={() => toggleOwner(owner)}>
                          <span className="marketplace-tag-filter-label">{owner}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Popover>
        </div>
        <div className="marketplace-controls-right">
          {allEnvironments.length > 0 && (activeTab === 'all' || activeTab === 'mcp') && (
            <div className="marketplace-env-pills">
              <span className="marketplace-env-pills-label">Environment</span>
              {allEnvironments.map((env) => (
                <button
                  key={env}
                  className={`marketplace-env-pill${selectedEnvironments.includes(env) ? ' active' : ''}`}
                  onClick={() => toggleEnvironment(env)}
                >
                  {env.toLowerCase()}
                </button>
              ))}
            </div>
          )}
          <div className="marketplace-sort">
            <label className="marketplace-sort-label">Sort by</label>
            <SelectMenu
              options={SORT_OPTIONS}
              value={sort}
              onChange={(val: string | string[]) => setSort((Array.isArray(val) ? val[0] : val) as 'recent' | 'alpha')}
              sizeMode="medium"
            />
          </div>
          <button
            className={`marketplace-refresh-button${!canRefresh ? ' disabled' : ''}`}
            onClick={() => { refresh(); showToast({ message: 'Marketplace refreshed', type: 'success', duration: 2000 }); }}
            disabled={!canRefresh}
            title="Refresh data"
          >
            <Icon icon="arrows-rotate" size="sm" />
            {!canRefresh && <span className="marketplace-refresh-countdown">{Math.ceil(cooldownRemaining / 1000)}s</span>}
          </button>
          <Link to="/create-skill" className="marketplace-add-skill-button">
            <Icon icon="plus" size="sm" />
            Add Skill
          </Link>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="marketplace-active-filters">
          {search && (
            <span className="marketplace-active-chip">
              Search: "{search}"
              <button className="marketplace-active-chip-remove" onClick={() => setSearch('')} aria-label="Remove search filter">×</button>
            </span>
          )}
          {selectedEnvironments.map((env) => (
            <span key={env} className="marketplace-active-chip">
              {env.toLowerCase()}
              <button className="marketplace-active-chip-remove" onClick={() => toggleEnvironment(env)} aria-label={`Remove ${env} filter`}>×</button>
            </span>
          ))}
          {selectedTags.map((tag) => (
            <span key={tag} className="marketplace-active-chip">
              {tag}
              <button className="marketplace-active-chip-remove" onClick={() => toggleTag(tag)} aria-label={`Remove ${tag} filter`}>×</button>
            </span>
          ))}
          {selectedOwners.map((owner) => (
            <span key={owner} className="marketplace-active-chip">
              {owner}
              <button className="marketplace-active-chip-remove" onClick={() => toggleOwner(owner)} aria-label={`Remove ${owner} filter`}>×</button>
            </span>
          ))}
          <button className="marketplace-clear-button" onClick={clearFilters}>Clear all</button>
        </div>
      )}

      <div className="marketplace-tabs-row">
        <div className="marketplace-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              className={`marketplace-tab${activeTab === tab.value ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span className="marketplace-result-count">
          {combinedItems.length} {combinedItems.length === 1 ? 'result' : 'results'}
        </span>
      </div>

      {skillsError && <p className="marketplace-error">Failed to load skills: {skillsError}</p>}
      {mcpError && <p className="marketplace-error">Failed to load MCP servers: {mcpError}</p>}

      {combinedItems.length > 0 ? (
        <>
          {activeTab === 'all' ? (
            <>
              {hasSkills && (
                <section className="marketplace-section">
                  <div className="marketplace-section-header">
                    <h2 className="marketplace-section-title"><Icon icon="bolt" size="sm" /> Skills</h2>
                    <span className="marketplace-section-count">{filteredSkills.length}</span>
                    {filteredSkills.length > 4 && (
                      <button className="marketplace-see-all" onClick={() => setActiveTab('skills')}>
                        See all
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    )}
                  </div>
                  <div className="marketplace-scroll-row" ref={attachWheelScroll}>
                    {filteredSkills.slice(0, 6).map((skill) => (
                      <div className="marketplace-scroll-item" key={`skill-${skill.id}`}>
                        <SkillCard skill={skill} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {hasServers && (
                <section className="marketplace-section">
                  <div className="marketplace-section-header">
                    <h2 className="marketplace-section-title"><Icon icon="computer" size="sm" /> MCP Servers</h2>
                    <span className="marketplace-section-count">{filteredServers.length}</span>
                    {filteredServers.length > 4 && (
                      <button className="marketplace-see-all" onClick={() => setActiveTab('mcp')}>
                        See all
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    )}
                  </div>
                  <div className="marketplace-scroll-row" ref={attachWheelScroll}>
                    {filteredServers.slice(0, 6).map((server) => (
                      <div className="marketplace-scroll-item" key={`mcp-${server.name}`}>
                        <McpServerCard server={server} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <section className="marketplace-results">
              <div className="marketplace-grid">
                {combinedItems.map((entry) =>
                  entry.type === 'skill' ? (
                    <SkillCard key={`skill-${(entry.item as Skill).id}`} skill={entry.item as Skill} />
                  ) : (
                    <McpServerCard key={`mcp-${(entry.item as McpServer).name}`} server={entry.item as McpServer} />
                  )
                )}
              </div>
            </section>
          )}
        </>
      ) : (
        <EmptyState
          title="No results match your search"
          description="Try different keywords or remove some filters to see more results."
          action={
            hasActiveFilters ? (
              <Button size="sm" mode="ghost" onClick={clearFilters}>
                Clear all filters
              </Button>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
